import React, { useCallback, useEffect, useRef, useState } from "react";
import { find, map, propEq } from "ramda";

import PanelLoadingIndicator from "components/PanelLoadingIndicator";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "auth/authActions";
import AddIcon from "@mui/icons-material/Add";
import { Container } from "@mui/material";
import { notBlank } from "../util/ext";
import { useAllModules, useRipEndpoints } from "./ripEndpointServices";
import RipEndpointListItem from "./RipEndpointListItem";
import RipEndpointDialog from "./RipEndpointDialog";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    panel: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: 60,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      padding: theme.spacing(4),
    },

    leftContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 200,
    },

    rightContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexGrow: 1,
    },

    title: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    newButton: {
      textTransform: "uppercase",
      color: "#127DA9",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },

    listContainer: {
      height: "calc(100vh - 275px)",
      overflow: "auto",
    },
  }));

const RipEndpointBrowse = () => {
  const [scrollVisible, setScrollVisible] = useState(true);

  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();

  // const userCacheRef = useRef(new Map());
  const [endpoints, setEndpoints] = useState([]);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState({});
  const classes = useStyles(scrollVisible)();

  const results = useRipEndpoints();
  useEffect(() => {
    if (results.isError && results.error.status === 401) {
      dispatch(logOut());
    }

    if (results.isSuccess && notBlank(results.data)) {
      setScrollVisible(true);
      setEndpoints(results.data);
    }
  }, [
    dispatch,
    auth,
    results.data,
    results.isError,
    results.error,
    results.isSuccess,
  ]);

  const [modules, setModules] = useState([]);
  const allModules = useAllModules();
  useEffect(() => {
    if (allModules.isSuccess && notBlank(allModules.data)) {
      setModules(allModules.data);
    }
  }, [allModules.isSuccess, allModules.data]);

  const findModule = useCallback(
    moduleId => {
      const module = find(propEq("id", moduleId), modules);
      return notBlank(module) ? module.name : moduleId;
    },
    [modules],
  );

  const handleDialogOnCancel = () => {
    setEditDialogVisible(false);
  };

  const handleDialogOnSave = async endpoint => {
    setCurrentEndpoint(endpoint);
    await results.refetch();
    setEditDialogVisible(false);
  };

  const handleAddAccountOnClick = () => {
    setCurrentEndpoint({});
    setEditDialogVisible(true);
  };

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(tableContainerEl.current)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      console.log(`Scroll Visible : ${scrollVisible}`);
      setScrollVisible(scrollVisible);
    }
  }, [tableContainerEl]);

  return (
    <Container id="sftp-account-browse-view" classes={{ root: classes.panel }}>
      <RipEndpointDialog
        open={editDialogVisible}
        endpoint={currentEndpoint}
        modules={modules}
        onSave={handleDialogOnSave}
        onClose={handleDialogOnCancel}
      />

      <div>
        <div>
          <div className={classes.toolbar}>
            <div className={classes.leftContainer}>
              <div className={classes.title}>RIP Endpoints</div>
            </div>

            <div>
              <div>
                <Button
                  variant={"text"}
                  startIcon={<AddIcon />}
                  classes={{ root: classes.newButton }}
                  onClick={() => handleAddAccountOnClick(true)}
                >
                  New RIP Endpoint
                </Button>
              </div>
            </div>
          </div>
        </div>

        {results.isLoading && <PanelLoadingIndicator />}
        {results.isSuccess && notBlank(endpoints) && (
          <Container classes={{ root: classes.panel }}>
            <Container ref={tableContainerEl} className={classes.listContainer}>
              <List>
                {map(endpoint => {
                  const selected =
                    endpoint.ripEndpointId === currentEndpoint.ripEndpointId ||
                    endpoint.ripEndpointName ===
                      currentEndpoint.ripEndpointName;
                  return (
                    <RipEndpointListItem
                      key={endpoint.ripEndpointId}
                      endpoint={endpoint}
                      selected={selected}
                      findModule={findModule}
                    />
                  );
                }, endpoints)}
              </List>
            </Container>
          </Container>
        )}
        {results.isError && <div>Error</div>}
      </div>
    </Container>
  );
};

export default RipEndpointBrowse;
